import { useCallback, useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates as batched } from 'react-dom';
import { useLocation, useRouteMatch } from 'react-router-dom';

import useHistoryQuery from 'hooks/useHistoryQuery.hook';
import HeaderSearchField from 'components/HeaderSearchField';

/**
 * Search logic:
 * If empty search input was filled - do not clear other filters.
 * If filled search input was cleard - do not clear other filters.
 * If input was filled after it was cleard - clear other filters.
 */
const HeaderSearch = (props) => {
  const { disabled } = props;

  const { value, setValue } = useHistoryQuery();
  const timeoutRef = useRef(null);
  const isSearchListPage = !!useRouteMatch({
    exact: true,
    path: '/:locale/:lang/products',
  });
  const location = useLocation();
  const searchTargetPath = !isSearchListPage ? '/products' : location.pathname;
  const waitRef = useRef(false);

  const [searchInputValue, setSearchInputValue] = useState(value?.query || '');
  const [commitedValue, setCommitedValue] = useState(value?.query || '');
  const [wasCleared, setWasCleared] = useState(false);
  const [textSelected, setTextSelected] = useState(false);
  const [inputElement, setInputElement] = useState(null);
  const inputRef = useRef();

  const searchInput = useCallback((el) => {
    setInputElement(el);
    inputRef.current = el;
  }, []);

  // Restore input selection when comming from another page to search page
  useEffect(() => {
    if (inputElement && isSearchListPage && !disabled) {
      const selectionStart = localStorage.getItem(
        'header_search_selection_start',
      );
      const selectionEnd = localStorage.getItem('header_search_selection_end');
      localStorage.removeItem('header_search_selection_start');
      localStorage.removeItem('header_search_selection_end');

      inputElement.focus();
      inputElement.selectionStart = selectionStart;
      inputElement.selectionEnd = selectionEnd;
    }
  }, [inputElement, isSearchListPage, disabled]);

  // Clear timeout callback on unmount
  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  // Listen for input text selection. If it fully selected, and new value has entered,
  // assum that input was fully cleared. This useEffect set [textSelected] value to true,
  // if input content is selected.
  useEffect(() => {
    if (inputElement && isSearchListPage) {
      const listenSelection = (e) => {
        const { selectionStart, selectionEnd, value: val } = inputElement;
        const selected =
          selectionStart === 0 && selectionEnd === val.length && val.length > 0;
        setTextSelected(selected);
      };
      inputElement.addEventListener('select', listenSelection);

      return () => {
        inputElement.removeEventListener('select', listenSelection);
      };
    }
  }, [inputElement, isSearchListPage]);

  // Listen input text unselect (any document mouse down, or any key press (than check)).
  // This useEffect set [textSelected] value to false, if selection has gone.
  useEffect(() => {
    if (!isSearchListPage) {
      return;
    }
    const listenMouseDown = () => {
      setTextSelected(false);
    };
    const listenKey = () => {
      const { selectionStart, selectionEnd } = inputElement;

      if (selectionEnd === selectionStart) {
        setTextSelected(false);
      }
    };
    document.addEventListener('mousedown', listenMouseDown);
    inputElement?.addEventListener('keyup', listenKey);

    return () => {
      document.removeEventListener('mousedown', listenMouseDown);
      inputElement?.removeEventListener('keyup', listenKey);
    };
  }, [inputElement, isSearchListPage]);

  const handleSearch = useCallback(
    (e) => {
      const s = e.target.value;
      const queryOptions = {};

      if (waitRef.current) {
        return;
      }
      setSearchInputValue(s);
      clearTimeout(timeoutRef.current);

      if (commitedValue && !s) {
        // Remember that input was cleared after filled state
        setWasCleared(true);
      }
      if (s && commitedValue !== s && textSelected) {
        // Input content was fully selected and new value has entered (pased or typed).
        // Assume that this is the same as fully cleared state. If timeout will work this
        // time, immediately clear other filters. Set [wasCleared] value to true for case
        // if timeout will not work this time.
        queryOptions.replace = true;
        setWasCleared(true);
      }
      timeoutRef.current = setTimeout(() => {
        // Use batched updates to avoid rerender after each setter fire
        if (!isSearchListPage) {
          waitRef.current = true;
        }
        batched(() => {
          if (s && wasCleared) {
            queryOptions.replace = true;
            // Reset
            setWasCleared(false);
          }
          if (!isSearchListPage) {
            queryOptions.pathname = searchTargetPath;
            queryOptions.replace = true;
          }
          if (inputRef.current) {
            const { selectionStart, selectionEnd } = inputRef.current;
            localStorage.setItem(
              'header_search_selection_start',
              selectionStart,
            );
            localStorage.setItem('header_search_selection_end', selectionEnd);
          }
          setValue('query', s, queryOptions);
          setCommitedValue(s);
        });
      }, 500);
    },
    [
      setValue,
      commitedValue,
      wasCleared,
      textSelected,
      isSearchListPage,
      searchTargetPath,
    ],
  );

  // Setup initial values from query search string
  useEffect(() => {
    if (value?.query !== commitedValue && isSearchListPage) {
      setSearchInputValue(value.query || '');
      setCommitedValue(value.query || '');
    }
  }, [value, commitedValue, isSearchListPage]);

  const onSearchSubmit = (e) => {
    e.preventDefault();
  };
  return (
    <form onSubmit={onSearchSubmit}>
      <HeaderSearchField
        disabled={(waitRef.current && !isSearchListPage) || disabled}
        value={searchInputValue}
        onChange={handleSearch}
        buttonProps={{ type: 'submit' }}
        inputRef={searchInput}
      />
    </form>
  );
};

export default HeaderSearch;
